/* Calendar-day estimates and an optional real-data viewer display. */
(() => {
  'use strict';
  if (customElements.get('aapka-product-details')) return;

  class AapkaProductDetails extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.pollTimer);
      clearInterval(this.dateTimer);
      this.request?.abort();
      this.events?.abort();
    }

    initialize() {
      this.events?.abort();
      clearTimeout(this.pollTimer);
      clearInterval(this.dateTimer);
      this.events = new AbortController();
      this.block = this.querySelector('[data-ae-real-viewers]');
      this.updateDeliveryDates();
      this.dateTimer = setInterval(() => {
        if (!document.hidden) this.updateDeliveryDates();
      }, 60000);
      document.addEventListener('visibilitychange', () => {
        clearTimeout(this.pollTimer);
        this.request?.abort();
        if (this.block) this.block.hidden = true;
        if (!document.hidden) {
          this.updateDeliveryDates();
          this.pollViewers();
        }
      }, { signal: this.events.signal });
      this.pollViewers();
    }

    updateDeliveryDates() {
      // Store audience is India; use India's calendar date, not a visitor's clock zone.
      const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit',
      }).formatToParts(new Date());
      const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
      const today = Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day));
      const formatter = new Intl.DateTimeFormat(document.documentElement.lang || 'en-IN', {
        timeZone: 'UTC', day: 'numeric', month: 'short',
      });
      this.querySelectorAll('[data-ae-delivery-date]').forEach((element) => {
        const minimum = Number(element.dataset.minDays);
        const maximum = Number(element.dataset.maxDays);
        if (!Number.isFinite(minimum) || !Number.isFinite(maximum)) return;
        const start = Math.max(0, Math.min(minimum, maximum));
        const end = Math.max(start, maximum, minimum);
        const first = formatter.format(new Date(today + start * 86400000));
        const last = formatter.format(new Date(today + end * 86400000));
        element.textContent = `${first === last ? first : `${first} – ${last}`} (est.)`;
      });
    }

    async pollViewers() {
      clearTimeout(this.pollTimer);
      if (!this.block || !this.isConnected || document.hidden) return;
      let endpoint;
      try {
        endpoint = new URL(this.block.dataset.endpoint, location.origin);
        if (endpoint.origin !== location.origin || !endpoint.pathname.startsWith('/apps/')) return;
        endpoint.searchParams.set('product_id', this.dataset.productId);
      } catch (_) { return; }
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const timeout = setTimeout(() => request.abort(), 8000);
      try {
        const response = await fetch(endpoint, {
          credentials: 'same-origin', cache: 'no-store',
          headers: { Accept: 'application/json' }, signal: request.signal,
        });
        if (!response.ok) throw new Error('Activity unavailable');
        const data = await response.json();
        const age = Date.now() - Date.parse(data.updated_at);
        if (String(data.product_id) !== this.dataset.productId ||
            !Number.isSafeInteger(data.count) || data.count < 0 ||
            !Number.isFinite(age) || age < -30000 || age > 120000) {
          throw new Error('Invalid or stale activity');
        }
        if (request.signal.aborted || !this.isConnected || document.hidden) return;
        this.block.querySelector('[data-ae-real-viewer-count]').textContent = String(data.count);
        this.block.hidden = data.count < 2;
      } catch (_) {
        if (this.isConnected && this.request === request) this.block.hidden = true;
      } finally {
        clearTimeout(timeout);
        if (this.request === request) {
          this.request = null;
          if (this.isConnected && !document.hidden) {
            this.pollTimer = setTimeout(() => this.pollViewers(), 30000);
          }
        }
      }
    }
  }

  customElements.define('aapka-product-details', AapkaProductDetails);
})();
